import rehypeAutolinkHeadings from "rehype-autolink-headings";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeSlug from "rehype-slug";
import rehypeStringify from "rehype-stringify";
import remarkFrontmatter from "remark-frontmatter";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

import { splitFrontmatter } from "./frontmatter";
import { contentSanitizeSchema } from "./sanitize";

/**
 * Markdown -> HTML (SPEC §13, TASK-403).
 *
 * **This is the only Markdown renderer in the project** (AGENTS.md §12). The
 * editor preview, the Astro build and the tests all call it, so a difference
 * between preview and production is not representable.
 *
 * The order of the plugins is the security model, so it is worth reading
 * top to bottom:
 *
 *   1. `remarkParse` / `remarkFrontmatter` / `remarkGfm` — Markdown to mdast.
 *      Frontmatter is *consumed*, never rendered.
 *   2. `remarkRehype` with `allowDangerousHtml` — mdast to hast, keeping raw
 *      HTML as `raw` nodes instead of dropping it.
 *   3. `rehypeRaw` — parse those raw nodes into real elements. This is the step
 *      that makes raw HTML *exist*, which is why it must be followed by:
 *   4. `rehypeSanitize` — strip everything not in `contentSanitizeSchema`.
 *      Nothing generated before this point is trusted.
 *   5. `rehypeSlug` / `rehypeHighlight` / `rehypeAutolinkHeadings` — add
 *      heading ids, syntax highlighting and the hover-reveal heading anchor.
 *      These run *after* sanitization because they are library-generated, not
 *      author input. The anchor step is optional (RenderOptions) — editing
 *      surfaces render without it so the marker never becomes content.
 *   6. `rehypeStringify` — hast to HTML.
 */

/** How a rendered document comes back from `renderDocument`. */
export interface RenderedDocument {
  readonly html: string;
  readonly body: string;
}

const base = () =>
  unified()
    .use(remarkParse)
    .use(remarkFrontmatter, ["yaml"])
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeSanitize, contentSanitizeSchema)
    .use(rehypeSlug)
    // `detect: false` keeps the highlighter from guessing; `ignoreMissing` means
    // an unknown language renders as plain code rather than failing the render.
    .use(rehypeHighlight, { detect: false, ignoreMissing: true })
    .use(rehypeStringify);

/** GitHub-style heading anchors: a hover-reveal `#`, not the whole heading. */
const withHeadingAnchors = base().use(rehypeAutolinkHeadings, {
  behavior: "append",
  properties: {
    className: ["anchor"],
    ariaHidden: "true",
    tabIndex: -1,
    dataPagefindIgnore: "",
  },
  content: { type: "text", value: "#" },
});

const withoutHeadingAnchors = base();

export interface RenderOptions {
  /**
   * Append a hover-reveal `#` anchor to each heading (the default).
   *
   * Turn off for editing surfaces: the anchor is presentation decoration, and
   * an editor that ingests it would grow a stray `#` at the end of every
   * heading on every load.
   */
  readonly autolink?: boolean;
}

/**
 * Render a Markdown body to sanitized HTML.
 *
 * Frontmatter, if present, is stripped rather than rendered — the caller
 * already has it as an object (see `parseFrontmatter`).
 */
export function renderMarkdown(markdown: string, options: RenderOptions = {}): string {
  const { body } = splitFrontmatter(markdown);
  const processor = options.autolink === false ? withoutHeadingAnchors : withHeadingAnchors;

  return String(processor.processSync(body));
}

/**
 * Render a complete document, returning both halves.
 *
 * The body is rendered even when the frontmatter fails to parse, so a preview
 * can still show something useful while the author fixes the metadata.
 */
export function renderDocument(markdown: string, options: RenderOptions = {}): RenderedDocument {
  const { body } = splitFrontmatter(markdown);
  const processor = options.autolink === false ? withoutHeadingAnchors : withHeadingAnchors;

  return { html: String(processor.processSync(body)), body };
}
